import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CircularProgress from '@mui/material/CircularProgress';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { AuthContext } from '../../authContext';
import AlertsCustom from '../Alerts';
import { translateBackendError } from '../../utils/translateBackendError';
import ConfirmDialog from '../ConfirmDialog';
import useEscapeToClose from '../../hooks/useEscapeToClose';
import ToggleSwitch from '../ToggleSwitch';
import { buildFileTree } from './skillFileTree';
import {
    SKILL_MAX_NAME_CHARS,
    SKILL_MAX_DESCRIPTION_CHARS,
    CONTENT_PREVIEW_MAX_BYTES,
    UnsupportedSkillFileError,
    buildSkillMdFrontmatter,
    extensionOf,
    extractSkillPreview,
    formatMegabytes,
    getSkillFileSizes,
    textByteLength,
} from './skillUpload';
import CodeHighlight, { CODE_EXTENSIONS } from './CodeHighlight';
import { parseCsv } from './csvPreview';
import { convertDocxToHtml, parseXlsxFirstSheet } from './officePreview';
import { parsePptxSlides } from './pptxPreview';

const SKILL_MD_PATH = 'SKILL.md';

// Bugfix (nesta tarefa): o manifesto de uma skill importada via .zip guarda o
// path do SKILL.md com a grafia/pasta originais do arquivo dentro do zip
// (backend/skills/helpers.py::_find_skill_md e' case-insensitive, mas nao
// renomeia o path armazenado) - so' skills de arquivo unico (.md avulso,
// FR-031) tem garantia de vir como exatamente "SKILL.md". Por isso o
// destaque do explorador de arquivos (`selectedPath === node.path`) so batia
// pra skills sem outros arquivos: com outros arquivos (import via .zip), o
// path real do SKILL.md podia divergir do literal `SKILL_MD_PATH`, mesmo com
// o conteudo certo ja exibido (GET /get_skill devolve `content` direto, sem
// depender do path). Resolve o path real a partir do manifesto (comparacao
// pelo nome do arquivo, case-insensitive); cai no literal só quando o
// manifesto ainda nao chegou (lista vazia).
function findSkillMdPath(files) {
    const entry = files.find((file) => file.path.split('/').pop().toLowerCase() === 'skill.md');
    return entry ? entry.path : SKILL_MD_PATH;
}

function formatDate(isoDate, locale) {
    if (!isoDate) return '';
    return new Date(isoDate).toLocaleDateString(locale || undefined, {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
    });
}

function isMarkdownPath(path) {
    return path.toLowerCase().endsWith('.md');
}

// Tarefa 8.2 (FR-056/FR-057): demais tipos de arquivo com visualizacao
// propria no painel de conteudo - .html/.csv ganham o mesmo toggle
// Previa/Codigo do .md (FR-051); arquivos da categoria "Codigo" da spec
// (CODE_EXTENSIONS, CodeHighlight.js) ganham so' syntax highlighting, sem
// toggle (nao ha uma forma "renderizada" distinta do proprio codigo).
function isHtmlPath(path) {
    return extensionOf(path) === 'html';
}

function isCsvPath(path) {
    return extensionOf(path) === 'csv';
}

function isCodePath(path) {
    return CODE_EXTENSIONS.has(extensionOf(path));
}

function hasViewToggle(path) {
    return isMarkdownPath(path) || isHtmlPath(path) || isCsvPath(path);
}

function stripFrontmatter(content) {
    return content.replace(/^---\r?\n[\s\S]*?\r?\n---\r?\n?/, '').trimStart();
}

// Tabela HTML generica a partir de um array de arrays (primeira linha vira
// cabecalho) - reaproveitada pelo modo "Previa" de .csv (FR-056, parseCsv) e
// pela primeira planilha de um .xlsx (Tarefa 8.3, FR-060, parseXlsxFirstSheet)
// - mesmo formato de linhas nos dois casos, so a fonte dos dados muda.
function DataTable({ rows, className, testId }) {
    if (!rows || rows.length === 0) return null;
    const [header, ...body] = rows;

    // Wrapper com scroll horizontal proprio (Tarefa 8.3) - uma planilha .xlsx
    // pode ter muito mais colunas que um .csv tipico; sem isso, uma tabela
    // larga forcaria a coluna de conteudo inteira (.skill-detail-content-scroll)
    // a esticar, quebrando o layout ao lado do explorador de arquivos.
    return (
        <div className="skill-detail-content-table-wrapper">
            <table className={className} data-testid={testId}>
                <thead>
                    <tr>
                        {header.map((cell, index) => <th key={index}>{cell}</th>)}
                    </tr>
                </thead>
                <tbody>
                    {body.map((cells, rowIndex) => (
                        <tr key={rowIndex}>
                            {cells.map((cell, cellIndex) => <td key={cellIndex}>{cell}</td>)}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function CsvTable({ text }) {
    return <DataTable rows={parseCsv(text)} className="skill-detail-content-csv-table" testId="skill-detail-content-csv-table" />;
}

// Cards de slide do preview de .pptx (Tarefa 8.4, FR-061) - um card por
// slide (numero + titulo, se houver + demais texto, se houver), sem
// nenhuma tentativa de layout/posicionamento fiel ao slide original
// (decisao tomada com o usuario - so' o texto extraido).
function PptxSlides({ slides }) {
    const { t } = useTranslation('chat');
    if (!slides || slides.length === 0) return null;

    return (
        <div className="skill-detail-content-pptx-slides" data-testid="skill-detail-content-pptx-slides">
            {slides.map((slide, index) => {
                const number = index + 1;
                const isEmpty = !slide.title && !slide.body;
                return (
                    <div className="skill-detail-pptx-slide-card" data-testid={`skill-detail-pptx-slide-${number}`} key={number}>
                        <p className="skill-detail-pptx-slide-number">{t('skills_detail_pptx_slide_label', { number })}</p>
                        {slide.title && (
                            <p className="skill-detail-pptx-slide-title" data-testid={`skill-detail-pptx-slide-${number}-title`}>
                                {slide.title}
                            </p>
                        )}
                        {slide.body && (
                            <p className="skill-detail-pptx-slide-body" data-testid={`skill-detail-pptx-slide-${number}-body`}>
                                {slide.body}
                            </p>
                        )}
                        {isEmpty && (
                            <p className="skill-detail-pptx-slide-empty">{t('skills_detail_pptx_slide_empty')}</p>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

// Tela "Detalhe da Skill" (Tarefa 6.1/6.2/6.3, specs/skills/plan.md, Grupo 6 -
// PBI 877: edicao de skills no ambiente de gestao). Figma: arquivo "Skills",
// node 101:600 ("Novos ajustes - Chat + Skills").
//
// Cabecalho: link "Voltar" + nome/autor/data (Tarefa 6.1); descricao
// (Tarefa 7.3, decisao tomada com o usuario ao revisar o popup de edicao
// direta - nao vinha do FR-034/FR-035 original nem do Figma consultado, mas
// segue o mesmo estilo truncado em 2 linhas ja usado por SkillCard)
// `[REVISADO nesta tarefa]`: saiu de dentro de skill-detail-header-info e
// passou a ser um paragrafo full-width logo abaixo do cabecalho, pra nao
// dividir espaco horizontal com skill-detail-header-actions. Cabecalho tem
// ainda toggle ativar/desativar (FR-043) e menu de opcoes de 3 pontos com
// "Editar skill no chat"/"Experimentar no chat" ligados via
// onEditInChat/onTryInChat (Tarefa 6.3/6.4) - "Excluir" (renomeado de
// "Desinstalar") exclui a skill de verdade, ver comentario de
// handleConfirmDelete abaixo. `[REVISADO nesta tarefa]`: o icone de compartilhar
// (skill-detail-share-button) saiu do cabecalho - "Compartilhar"
// (reaproveita o ShareSkillModal ja existente via onShare, sem nenhuma
// mudanca nele) passou a ser o primeiro item do menu de 3 pontos
// (skill-detail-menu-share), so pro dono, mesma regra de antes.
//
// Tarefa 7.3 (FR-048): item "Editar skill" (skill-detail-menu-edit-direct)
// abre um popup de edicao direta (nome/descricao/conteudo do SKILL.md, texto
// cru), visual do Figma node 118:484 - caminho adicional ao conversacional
// (Editar skill no chat), nao o substitui; os dois atualizam a mesma skill
// via PUT /put_skill.
//
// Tarefa 7.6 (FR-051): toggle "Previa"/"Codigo" no painel de conteudo, so
// para arquivos .md - "Previa" (padrao) e' o markdown processado ja
// existente, "Codigo" e' o texto cru (mesmo frontmatter removido de
// "Previa"). Reinicia pra "Previa" a cada troca de arquivo selecionado.
//
// `[NOVO nesta tarefa]`: botao "Baixar" (skill-detail-download-button), logo
// apos skill-detail-header-info, no mesmo grupo de acoes do
// toggle/compartilhar/menu (Figma node 134:2015) - reaproveita
// handleDownloadSkill (SkillsManagerModal/index.js) via onDownload, mesma
// rota GET /get_skill_download que antes so' existia no icone de download do
// SkillCard (removido desta tela, ver comentario de SkillCard.js). So'
// visivel pra quem pode baixar a skill (canDownload abaixo) - dono, ou
// acesso via compartilhamento explicito; nunca em skills publicas da "TPF"
// vistas por quem nao e dono. Tambem substitui o botao de download por
// arquivo individual que existia no aviso de "arquivo grande demais pra
// pre-visualizar" (skill-detail-download-large-file, removido) - agora so'
// existe download da skill inteira, nunca de um arquivo isolado.
//
// `[REVISADO em 2026-08-12, 2x]`: o toggle passou pelo SkillCard e voltou
// pra aqui no mesmo dia - decisao final do usuario foi manter aqui, mas
// abrindo esta tela pra QUALQUER usuario com acesso (nao so o dono), ja que
// o toggle e por usuario (FR-043) e nao pode ficar preso a quem edita.
// index.js::getCardOnSelect abre esta tela pra quem nao e dono ao clicar no
// card (em vez de inserir a skill no chat direto, FR-023). `isOwner` abaixo
// esconde "Compartilhar" e os itens "Editar skill no chat"/"Excluir" do
// menu pra quem nao e dono - only "Experimentar no chat" e o toggle sao de
// todo mundo. Corpo (Tarefa 6.2): explorador de arquivos (esquerda) +
// conteudo do arquivo selecionado (direita) - SKILL.md selecionado por
// padrao.
// Mesmos perfis de NATIVE_CONTENT_ROLES no backend (native_skills.py).
const NATIVE_CONTENT_LEVELS = ['dev', 'mod'];

export default function SkillDetailView({ skill, onBack, onShare, onDownload, onTryInChat, onEditInChat, onActiveChanged, onSkillUpdated, onDeleted }) {
    const { t, i18n } = useTranslation('chat');
    const { email, accessToken, level } = useContext(AuthContext);
    const { name, description, updated_at, owner, skill_id } = skill;
    const isOwner = owner?.email?.toLowerCase() === email?.toLowerCase();
    // Skill nativa (backend/skills_helpers/native_skills.py): qualquer um ve e
    // usa, mas o conteudo (SKILL.md, arquivos) so' moderador le - o backend
    // devolve 403 pros demais, aqui so' evitamos a chamada e mostramos aviso.
    const canReadContent = !skill.is_native || NATIVE_CONTENT_LEVELS.includes(level);
    // Botao "Baixar" (nova tarefa, Figma node 134:2015): so o dono ("Minhas
    // Skills") e quem tem a skill compartilhada explicitamente
    // ("Compartilhadas") podem baixar - skills publicas da "TPF" nao, salvo
    // se o proprio dono estiver vendo a sua em "Minhas Skills". A aba
    // "compartilhadas" do backend (backend/skills/helpers.py::_TAB_QUERIES)
    // so' devolve skills com visibility <> 'public', e "tpf" so' devolve
    // visibility = 'public' - por isso basta olhar isOwner/visibility aqui,
    // sem precisar saber de qual aba esta skill foi aberta.
    const canDownload = isOwner;

    const [loading, setLoading] = useState(true);
    const [files, setFiles] = useState([]);
    const [selectedPath, setSelectedPath] = useState(SKILL_MD_PATH);
    // Path real do SKILL.md dentro do manifesto atual (ver findSkillMdPath
    // acima) - recalculado sempre que `files` muda, usado em vez do literal
    // `SKILL_MD_PATH` sempre que o codigo precisa apontar pro "arquivo
    // SKILL.md desta skill" de verdade (selecao inicial/pos-substituicao,
    // destaque no explorador).
    const skillMdPath = useMemo(() => findSkillMdPath(files), [files]);
    // Cache local por path (Tarefa 6.2) - SKILL.md ja chega pronto de
    // GET /get_skill (busca abaixo); qualquer outro arquivo so e buscado via
    // GET /get_skill_file na primeira vez que e selecionado.
    const [fileCache, setFileCache] = useState({});
    const [fileLoading, setFileLoading] = useState(false);
    const [collapsedFolders, setCollapsedFolders] = useState({});

    // Tarefa 6.6 (FR-043): estado ativo/inativo, por par (usuario, skill) -
    // nunca um campo unico na skill. Comeca "ativa" (`true`) até GET
    // /get_skill responder, mesmo default do backend quando nao ha linha em
    // skill_user_state.
    const [active, setActive] = useState(skill.active ?? true);

    // Alterna de forma otimista (mesmo padrao do compartilhamento,
    // ShareSkillModal) - reverte se POST /post_skill_active falhar.
    // onActiveChanged avisa o index.js pra reconciliar o cache por aba
    // (skillsByTab), sem precisar refazer a busca ao voltar pra grade.
    const handleToggleActive = () => {
        const next = !active;
        setActive(next);
        onActiveChanged?.(skill_id, next);
        fetch(`${process.env.REACT_APP_API_URL}/post_skill_active`, {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${accessToken}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ email, skill_id, active: next }),
        })
            .then((response) => {
                if (!response.ok) {
                    setActive(!next);
                    onActiveChanged?.(skill_id, !next);
                }
            })
            .catch(() => {
                setActive(!next);
                onActiveChanged?.(skill_id, !next);
            });
    };

    // Menu de opcoes de 3 pontos (Tarefa 6.3, FR-036) - mesmo mecanismo de
    // fechar ao clicar fora ja usado pelo dropdown "/" (slashMenuRef,
    // UnifiedChat.js).
    const menuRef = useRef(null);
    const [menuOpen, setMenuOpen] = useState(false);

    useEffect(() => {
        if (!menuOpen) return;
        function handleClickOutside(event) {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setMenuOpen(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [menuOpen]);

    // Substituir skill (Tarefa 7.4, FR-049; renomeado de "Reimportar arquivo"
    // por decisao do usuario) - substitui o manifesto inteiro da skill (mesma
    // semantica simples de "Adicionar Skill", Tarefa 3.3: sem mesclar com
    // arquivos antigos). Reaproveita a validacao de limite de tamanho ja
    // existente do lado do cliente (skillUpload.js, Tarefa 7.7) antes de
    // qualquer envio - so o dono ve essa opcao no menu (isOwner). Um popup de
    // confirmacao explicita (ConfirmDialog, novo - nao havia precedente de
    // confirmacao de 2 botoes no projeto) aparece antes de efetivar a
    // substituicao, ja que ela descarta o conteudo anterior.
    const replaceInputRef = useRef(null);
    const [pendingReplaceFile, setPendingReplaceFile] = useState(null);
    const [replacing, setReplacing] = useState(false);
    const [alert, setAlert] = useState(null);

    // Excluir skill (renomeado de "Desinstalar", que ate aqui era so
    // placeholder sem efeito) - so o dono ve essa opcao no menu (isOwner).
    // Mesmo padrao de popup de confirmacao ja usado por "Substituir skill"
    // acima (ConfirmDialog). `[REVISADO nesta tarefa]`: ao confirmar, o
    // retorno visual (fechar o Detalhe e sumir da grade) NAO espera a
    // resposta do backend - onDeleted (SkillsManagerModal/index.js) e' quem
    // dispara o POST /delete_skill de fato, depois de ja ter atualizado a UI
    // (mesmo trade-off ja aceito por handleSaveEdit/ShareSkillModal: erro do
    // backend so mostra alerta, sem trazer o card de volta sozinho - reabrir
    // o "Gerenciador de Skills" refaz a busca). Fica em index.js, nao aqui,
    // porque esta tela e desmontada assim que onDeleted roda.
    const [pendingDelete, setPendingDelete] = useState(false);

    const handleCancelDelete = () => setPendingDelete(false);

    const handleConfirmDelete = () => {
        setPendingDelete(false);
        onDeleted?.(skill_id);
    };

    // Extraído (achado da auditoria retroativa, specs/skills/plan.md, 2026-09-15) pra ser
    // reaproveitado tanto pelo `<input type="file">` (clique) quanto pelo drag-and-drop.
    const processReplaceFile = async (file) => {
        if (!file) return;

        try {
            // getSkillFileSizes so' precisa rodar aqui pelo efeito colateral
            // de levantar UnsupportedSkillFileError pra um formato de
            // arquivo que nao seja .md/.zip/.skill - a allow-list de
            // extensoes dos arquivos DENTRO do upload (FR-054) foi removida
            // pela FR-075 (Tarefa 11.3).
            await getSkillFileSizes(file);
        } catch (error) {
            const message = error instanceof UnsupportedSkillFileError
                ? t('skills_manager_add_unsupported_format')
                : t('skills_detail_replace_error_title');
            setAlert({ type: 'error', title: t('skills_detail_replace_error_title'), message });
            return;
        }

        setPendingReplaceFile(file);
    };

    const handleReplaceFileChange = (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        processReplaceFile(file);
    };

    const handleCancelReplace = () => setPendingReplaceFile(null);

    const handleConfirmReplace = async () => {
        const file = pendingReplaceFile;
        setPendingReplaceFile(null);
        if (!file) return;

        // Snapshot do estado atual - permite desfazer o otimista abaixo se o
        // PUT /put_skill for recusado (decisao do usuario: diferente de
        // outros fluxos otimistas do projeto, que so alertam sem reverter,
        // "Substituir skill" descarta o conteudo anterior por completo -
        // FR-049 - entao uma pre-visualizacao que nunca foi salva de verdade
        // e' mais arriscada de deixar na tela do que nos outros casos).
        const previousFiles = files;
        const previousFileCache = fileCache;
        const previousSelectedPath = selectedPath;
        const previousCollapsedFolders = collapsedFolders;
        const previousSkill = { name, description };

        setReplacing(true);

        // Otimista - le o arquivo no navegador (sem round-trip ao backend) e
        // mostra o resultado aparente na hora: conteudo, arvore de arquivos
        // e, se o frontmatter trouxer nome/descricao diferentes, o
        // cabecalho tambem (mesmo bug corrigido no backend nesta tarefa).
        // Mesmo padrao ja usado por handleSaveEdit; reconciliado com a
        // resposta real abaixo, ou desfeito no catch/erro.
        const preview = await extractSkillPreview(file).catch(() => null);
        if (preview) {
            const previewSkillMdPath = findSkillMdPath(preview.files);
            setFiles(preview.files);
            setFileCache({ [previewSkillMdPath]: { content: preview.content, is_text: true } });
            setSelectedPath(previewSkillMdPath);
            setCollapsedFolders({});
            if (preview.name || preview.description) {
                onSkillUpdated?.({
                    ...skill,
                    ...(preview.name ? { name: preview.name } : {}),
                    ...(preview.description ? { description: preview.description } : {}),
                });
            }
        }

        const revertOptimistic = () => {
            setFiles(previousFiles);
            setFileCache(previousFileCache);
            setSelectedPath(previousSelectedPath);
            setCollapsedFolders(previousCollapsedFolders);
            onSkillUpdated?.({ ...skill, ...previousSkill });
        };

        try {
            const formData = new FormData();
            formData.append('email', email);
            formData.append('skill_id', skill_id);
            formData.append('file', file);

            const response = await fetch(`${process.env.REACT_APP_API_URL}/put_skill`, {
                method: 'PUT',
                headers: { Authorization: `Bearer ${accessToken}` },
                body: formData,
            });
            const data = await response.json().catch(() => ({}));

            if (!response.ok) {
                revertOptimistic();
                setAlert({
                    type: 'error',
                    title: t('skills_detail_replace_error_title'),
                    message: translateBackendError(data.error) || t('skills_detail_replace_error_title'),
                });
                return;
            }

            // Reconciliacao com a resposta real - pode divergir do preview
            // otimista (ex.: manifesto reempacotado com paths/blob_refs
            // reais). Nome/descricao (FR bugfix: update_skill agora le os
            // dois do frontmatter quando 'new_name'/'description' nao vem
            // explicitos neste form) tambem vem daqui, sem depender so' do
            // parsing feito no cliente.
            const newFiles = data.files || [];
            const newSkillMdPath = findSkillMdPath(newFiles);
            setFiles(newFiles);
            setFileCache({ [newSkillMdPath]: { content: data.content || '', is_text: true } });
            setSelectedPath(newSkillMdPath);
            setCollapsedFolders({});
            onSkillUpdated?.(data);
        } catch (error) {
            revertOptimistic();
            setAlert({
                type: 'error',
                title: t('skills_detail_replace_error_title'),
                message: t('skills_detail_replace_error_title'),
            });
        } finally {
            setReplacing(false);
        }
    };

    // Edicao direta (Tarefa 7.3, FR-048) - popup com nome/descricao/conteudo
    // editaveis, sem depender da conversa guiada por IA (User Story 6). Os
    // dois caminhos coexistem e atualizam a mesma skill via PUT /put_skill;
    // aqui, o SKILL.md e' sempre reconstruido inteiro (frontmatter + corpo
    // editado) e reenviado via `file`, mesmo padrao ja usado pelo fluxo
    // conversacional (skill_editor_agent.py::finalizar_edicao) - garante que
    // as mesmas validacoes (MAX_NAME_CHARS, unicidade de nome) rodem em
    // update_skill, sem duplicar logica de parsing aqui.
    // `name`/`description` vem sempre do prop `skill` (nao um estado
    // separado) - ja reflete qualquer edicao anterior porque o pai
    // (SkillsManagerModal/index.js) atualiza `detailSkill` a cada sucesso.
    const [editing, setEditing] = useState(false);
    const [editName, setEditName] = useState('');
    const [editDescription, setEditDescription] = useState('');
    const [editContent, setEditContent] = useState('');
    const [savingEdit, setSavingEdit] = useState(false);

    const handleOpenEdit = () => {
        setEditName(name);
        setEditDescription(description || '');
        setEditContent(stripFrontmatter(fileCache[skillMdPath]?.content || ''));
        setEditing(true);
    };

    const handleCancelEdit = () => {
        if (savingEdit) return;
        setEditing(false);
    };

    const handleEditOverlayClick = (event) => {
        if (event.target === event.currentTarget) handleCancelEdit();
    };

    // Achado de acessibilidade (specs/skills/plan.md, 2026-09-15): popup "Editar skill"
    // também não respondia ao Esc. `handleCancelEdit` já ignora enquanto `savingEdit`.
    useEscapeToClose(handleCancelEdit, editing);

    const handleSaveEdit = async () => {
        if (savingEdit) return;

        const trimmedName = editName.trim();
        const trimmedDescription = editDescription.trim();

        if (trimmedName.length > SKILL_MAX_NAME_CHARS) {
            setAlert({
                type: 'error',
                title: t('skills_manager_name_limit_exceeded_title'),
                message: t('skills_detail_edit_name_limit_message', {
                    chars: trimmedName.length,
                    limit: SKILL_MAX_NAME_CHARS,
                }),
            });
            return;
        }

        // Tarefa 11.1 (FR-077): mesmo padrao do limite de nome acima.
        if (trimmedDescription.length > SKILL_MAX_DESCRIPTION_CHARS) {
            setAlert({
                type: 'error',
                title: t('skills_manager_description_limit_exceeded_title'),
                message: t('skills_detail_edit_description_limit_message', {
                    chars: trimmedDescription.length,
                    limit: SKILL_MAX_DESCRIPTION_CHARS,
                }),
            });
            return;
        }

        const skillMd = `${buildSkillMdFrontmatter(trimmedName, trimmedDescription)}\n${editContent.trim()}\n`;

        // Passou nas validacoes locais - fecha o popup e ja atualiza a
        // renderizacao (subtitulo de descricao + nome no cabecalho, conteudo
        // do SKILL.md exibido) de forma otimista com o que o usuario digitou,
        // sem esperar a resposta do PUT /put_skill - mesmo padrao ja usado
        // por ShareSkillModal::handleConfirm (Tarefa 5.10). onSkillUpdated
        // (mesmo callback do sucesso, so que com um objeto otimista em vez
        // da resposta real) avisa SkillsManagerModal/index.js pra reconciliar
        // skillsByTab/detailSkill na hora. `files` (lista de paths) fica de
        // fora do otimista de proposito - so muda com a resposta confirmada.
        // Editar nao mexe mais nos outros arquivos da skill (o backend
        // preserva o manifesto, ver `preservar_arquivos` abaixo), entao o
        // otimista tambem NAO descarta o resto da arvore aqui: so' troca o
        // conteudo em cache do proprio SKILL.md e deixa as pastas como
        // estavam (inclusive o que o usuario tinha recolhido).
        setEditing(false);
        setFileCache((anterior) => ({ ...anterior, [skillMdPath]: { content: skillMd, is_text: true } }));
        setSelectedPath(skillMdPath);
        onSkillUpdated?.({ ...skill, name: trimmedName, description: trimmedDescription });

        setSavingEdit(true);
        try {
            const formData = new FormData();
            formData.append('email', email);
            formData.append('skill_id', skill_id);
            formData.append('new_name', trimmedName);
            formData.append('description', trimmedDescription);
            formData.append('file', new Blob([skillMd], { type: 'text/markdown' }), 'SKILL.md');
            // Este popup manda SO' o SKILL.md - sem declarar a intencao, o
            // backend trata `file` como pacote completo e apaga todo o resto
            // da skill (scripts/, references/, assets/...). Bug real relatado
            // em teste (set/2026): uma skill com varias pastas ficou so' com
            // o .md depois da primeira edicao por aqui. "Substituir skill"
            // (handleConfirmReplace) NAO manda este campo de proposito - la'
            // descartar o conteudo anterior e' justamente o comportamento
            // esperado (FR-049).
            formData.append('preservar_arquivos', 'true');

            const response = await fetch(`${process.env.REACT_APP_API_URL}/put_skill`, {
                method: 'PUT',
                headers: { Authorization: `Bearer ${accessToken}` },
                body: formData,
            });
            const data = await response.json().catch(() => ({}));

            if (!response.ok) {
                // Erro do backend (ex.: 409 de nome em conflito) - so um
                // alerta avulso, sem reverter o otimista acima (mesmo
                // trade-off ja aceito por ShareSkillModal - o usuario decide
                // reabrir/reeditar se notar o erro).
                setAlert({
                    type: 'error',
                    title: t('skills_detail_edit_error_title'),
                    message: translateBackendError(data.error) || t('skills_detail_edit_error_title'),
                });
                return;
            }

            // Reconciliacao com a resposta real do backend (pode divergir do
            // otimista - ex.: trimming diferente do lado do servidor, ou o
            // manifesto de arquivos, que o otimista acima nao mexeu).
            const newFiles = data.files || [];
            const newSkillMdPath = findSkillMdPath(newFiles);
            setFiles(newFiles);
            setFileCache({ [newSkillMdPath]: { content: data.content || '', is_text: true } });
            setSelectedPath(newSkillMdPath);
            onSkillUpdated?.(data);
        } catch (error) {
            setAlert({
                type: 'error',
                title: t('skills_detail_edit_error_title'),
                message: t('skills_detail_edit_error_title'),
            });
        } finally {
            setSavingEdit(false);
        }
    };

    // Toggle "Prévia"/"Código" (Tarefa 7.6, FR-051) - originalmente so' pra
    // .md (SKILL.md ou qualquer outro dentro do manifesto): "Previa" e' o
    // markdown processado (comportamento ja existente); "Codigo" e' o texto
    // cru, mesmo frontmatter removido de "Previa" (consistente com o preview
    // do card de resultado no chat, SkillResultCard). `[ESTENDIDO na Tarefa
    // 8.2, FR-056]`: mesmo estado/toggle agora tambem cobre .html ("Previa" =
    // <iframe> renderizando o HTML de verdade; "Codigo" = fonte com syntax
    // highlighting) e .csv ("Previa" = tabela HTML parseada; "Codigo" = texto
    // cru) - ver hasViewToggle acima. Reinicia pra "Previa" sempre que o
    // arquivo selecionado muda - decisao tomada com o usuario para o padrao
    // ser sempre a versao processada ao abrir/trocar de arquivo.
    const [viewMode, setViewMode] = useState('preview');

    useEffect(() => {
        setViewMode('preview');
    }, [selectedPath]);

    // Busca o detalhe completo (conteudo do SKILL.md + manifesto de
    // arquivos) ao abrir - `skill` recebido do SkillsManagerModal so tem os
    // campos de resumo da listagem (GET /get_skills), sem `content`/`files`.
    useEffect(() => {
        let cancelled = false;
        setSelectedPath(SKILL_MD_PATH);
        setCollapsedFolders({});
        if (!canReadContent) {
            setLoading(false);
            return undefined;
        }
        setLoading(true);

        fetch(`${process.env.REACT_APP_API_URL}/get_skill?email=${encodeURIComponent(email)}&skill_id=${encodeURIComponent(skill_id)}`, {
            headers: { Authorization: `Bearer ${accessToken}` },
        })
            .then((response) => (response.ok ? response.json() : null))
            .then((data) => {
                if (cancelled) return;
                const loadedFiles = data?.files || [];
                const loadedSkillMdPath = findSkillMdPath(loadedFiles);
                setFiles(loadedFiles);
                setSelectedPath(loadedSkillMdPath);
                setFileCache({ [loadedSkillMdPath]: { content: data?.content || '', is_text: true } });
                setActive(data?.active ?? true);
            })
            .catch(() => {
                if (!cancelled) {
                    setFiles([]);
                    setFileCache({ [SKILL_MD_PATH]: { content: '', is_text: true } });
                }
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [skill_id]);

    // Busca o conteudo de um arquivo diferente de SKILL.md sob demanda (GET
    // /get_skill_file), cacheado por path para nao rebuscar ao selecionar o
    // mesmo arquivo de novo.
    useEffect(() => {
        if (loading || fileCache[selectedPath] !== undefined) return;

        let cancelled = false;
        setFileLoading(true);

        fetch(`${process.env.REACT_APP_API_URL}/get_skill_file?email=${encodeURIComponent(email)}&skill_id=${encodeURIComponent(skill_id)}&path=${encodeURIComponent(selectedPath)}`, {
            headers: { Authorization: `Bearer ${accessToken}` },
        })
            .then((response) => (response.ok ? response.json() : { content: null, is_text: false }))
            .then((data) => {
                if (!cancelled) {
                    // Tarefa 6.9/8.1/8.3/8.4: repassa is_image/is_pdf/
                    // is_docx/is_xlsx/is_pptx/content_base64/mimetype tambem
                    // (alem de content/is_text ja existentes) - sem eles,
                    // esses arquivos cairiam no aviso generico de
                    // "pre-visualizacao nao suportada" em vez do preview real.
                    setFileCache((prev) => ({
                        ...prev,
                        [selectedPath]: {
                            content: data.content,
                            is_text: data.is_text,
                            is_image: data.is_image,
                            is_pdf: data.is_pdf,
                            is_docx: data.is_docx,
                            is_xlsx: data.is_xlsx,
                            is_pptx: data.is_pptx,
                            content_base64: data.content_base64,
                            mimetype: data.mimetype,
                        },
                    }));
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setFileCache((prev) => ({ ...prev, [selectedPath]: { content: null, is_text: false } }));
                }
            })
            .finally(() => {
                if (!cancelled) setFileLoading(false);
            });

        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedPath, loading]);

    // Preview de .docx/.xlsx (Tarefa 8.3, FR-059/FR-060) - a conversao
    // (mammoth/xlsx) acontece no CLIENTE a partir do `content_base64` ja
    // buscado acima, entao roda num efeito separado, keyed em `selectedPath`
    // (nao em `fileCache[selectedPath]` inteiro, que muda de identidade a
    // cada fetch mesmo pro mesmo path). `officePreview.path` guarda pra qual
    // arquivo o resultado abaixo pertence, pra nao mostrar por um instante o
    // preview do arquivo anterior enquanto o novo ainda converte.
    const [officePreview, setOfficePreview] = useState(null);
    const [officePreviewLoading, setOfficePreviewLoading] = useState(false);

    useEffect(() => {
        const currentFile = fileCache[selectedPath];
        const kind = currentFile?.is_docx ? 'docx' : currentFile?.is_xlsx ? 'xlsx' : currentFile?.is_pptx ? 'pptx' : null;
        if (!kind) return;

        let cancelled = false;
        setOfficePreviewLoading(true);

        (async () => {
            try {
                if (kind === 'docx') {
                    const html = await convertDocxToHtml(currentFile.content_base64);
                    if (!cancelled) setOfficePreview({ path: selectedPath, kind, html });
                } else if (kind === 'xlsx') {
                    const rows = parseXlsxFirstSheet(currentFile.content_base64);
                    if (!cancelled) setOfficePreview({ path: selectedPath, kind, rows });
                } else {
                    const slides = await parsePptxSlides(currentFile.content_base64);
                    if (!cancelled) setOfficePreview({ path: selectedPath, kind, slides });
                }
            } catch (error) {
                if (!cancelled) setOfficePreview({ path: selectedPath, kind, error: true });
            } finally {
                if (!cancelled) setOfficePreviewLoading(false);
            }
        })();

        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedPath, fileCache[selectedPath]]);

    const toggleFolder = (path) => {
        setCollapsedFolders((prev) => ({ ...prev, [path]: !prev[path] }));
    };

    const renderNodes = (nodes) => nodes.map((node) => {
        if (node.type === 'folder') {
            const collapsed = !!collapsedFolders[node.path];
            return (
                <div key={node.path} className="skill-detail-folder">
                    <button
                        type="button"
                        className="skill-detail-folder-toggle"
                        onClick={() => toggleFolder(node.path)}
                        data-testid={`skill-detail-folder-${node.path}`}
                    >
                        <img
                            src="/imagens/chevron-down.svg"
                            alt=""
                            className={`skill-detail-folder-icon${collapsed ? ' skill-detail-folder-icon--collapsed' : ''}`}
                        />
                        {node.name}
                    </button>
                    {!collapsed && (
                        <div className="skill-detail-folder-children">
                            {renderNodes(node.children)}
                        </div>
                    )}
                </div>
            );
        }
        return (
            <button
                type="button"
                key={node.path}
                className={`skill-detail-file-item${selectedPath === node.path ? ' skill-detail-file-item--active' : ''}`}
                onClick={() => setSelectedPath(node.path)}
                data-testid={`skill-detail-file-${node.path}`}
            >
                {node.name}
            </button>
        );
    });

    const current = fileCache[selectedPath];
    // Bugfix pos-Tarefa 7.7: acima deste tamanho (bem menor que o limite de
    // upload, 20MB), nem "Previa" nem "Codigo" tentam renderizar o conteudo
    // inteiro - travava a aba (ver skillUpload.js::CONTENT_PREVIEW_MAX_BYTES).
    // `[REVISADO nesta tarefa]`: o aviso ja nao vem mais acompanhado de um
    // botao de download individual daquele arquivo (skill-detail-download-large-file,
    // removido) - baixar a skill inteira agora e' o botao "Baixar" do
    // cabecalho (skill-detail-download-button), unica forma de download desta
    // tela.
    const currentTextBytes = current?.is_text ? textByteLength(current.content) : 0;
    const contentTooLargeToPreview = currentTextBytes > CONTENT_PREVIEW_MAX_BYTES;

    return (
        <div className="skill-detail-view" data-testid="skill-detail-view">
            <button
                type="button"
                className="skill-detail-back"
                onClick={onBack}
                data-testid="skill-detail-back"
            >
                <img src="/imagens/left-arrow-return.svg" alt="" className="skill-detail-back-icon" />
                {t('skills_detail_back')}
            </button>

            <div className="skill-detail-header">
                <div className="skill-detail-header-info">
                    <p className="skill-detail-name" data-testid="skill-detail-name">/{name}</p>
                    <div className="skill-detail-meta">
                        <span className="skill-detail-author" data-testid="skill-detail-author">{owner?.name}</span>
                        <span className="skill-detail-date-separator" aria-hidden="true" />
                        <span className="skill-detail-date" data-testid="skill-detail-date">
                            {formatDate(updated_at, i18n.language)}
                        </span>
                    </div>
                </div>

                <div className="skill-detail-header-actions">
                    {canDownload && (
                        <button
                            type="button"
                            className="skill-detail-download-button"
                            onClick={onDownload}
                            data-testid="skill-detail-download-button"
                        >
                            {t('skills_detail_download_button')}
                        </button>
                    )}

                    <div className="skill-detail-toggle" data-testid="skill-detail-toggle">
                        {/* Desabilitado enquanto `loading` - GET /get_skill ainda pode
                            resolver e sobrescrever `active` (setActive(data.active)) depois
                            de um clique otimista que aconteceu antes dele, revertendo o
                            toggle silenciosamente (corrida real encontrada via Cypress,
                            2026-08-12). */}
                        <ToggleSwitch isChecked={active} onToggle={handleToggleActive} disabled={loading} />
                    </div>

                    <div className="skill-detail-menu-wrapper" ref={menuRef}>
                        <button
                            type="button"
                            className="skill-detail-menu-button"
                            aria-label={t('skills_detail_options_menu')}
                            onClick={() => setMenuOpen((prev) => !prev)}
                            data-testid="skill-detail-menu-button"
                        >
                            <img
                                src={menuOpen ? '/imagens/three-points-activated.svg' : '/imagens/three-points.svg'}
                                alt=""
                                className="skill-detail-menu-icon"
                            />
                        </button>

                        {menuOpen && (
                            <div className="skill-detail-menu" data-testid="skill-detail-menu">
                                {isOwner && (
                                    <button
                                        type="button"
                                        className="skill-detail-menu-item"
                                        onClick={() => {
                                            setMenuOpen(false);
                                            onShare();
                                        }}
                                        data-testid="skill-detail-menu-share"
                                    >
                                        {t('skill_share_button')}
                                    </button>
                                )}
                                {isOwner && (
                                    <button
                                        type="button"
                                        className="skill-detail-menu-item"
                                        disabled={loading}
                                        onClick={() => {
                                            if (loading) return;
                                            setMenuOpen(false);
                                            handleOpenEdit();
                                        }}
                                        data-testid="skill-detail-menu-edit-direct"
                                    >
                                        {t('skills_detail_menu_edit_direct')}
                                    </button>
                                )}
                                {isOwner && (
                                    <button
                                        type="button"
                                        className="skill-detail-menu-item"
                                        onClick={() => {
                                            setMenuOpen(false);
                                            onEditInChat();
                                        }}
                                        data-testid="skill-detail-menu-edit-chat"
                                    >
                                        {t('skills_detail_menu_edit_chat')}
                                    </button>
                                )}
                                {isOwner && (
                                    <button
                                        type="button"
                                        className="skill-detail-menu-item"
                                        disabled={replacing}
                                        onClick={() => {
                                            if (replacing) return;
                                            setMenuOpen(false);
                                            replaceInputRef.current?.click();
                                        }}
                                        data-testid="skill-detail-menu-replace"
                                    >
                                        {t('skills_detail_menu_replace')}
                                    </button>
                                )}
                                <button
                                    type="button"
                                    className="skill-detail-menu-item"
                                    disabled={loading || !active}
                                    onClick={() => {
                                        if (loading || !active) return;
                                        setMenuOpen(false);
                                        onTryInChat();
                                    }}
                                    data-testid="skill-detail-menu-try-chat"
                                >
                                    {t('skills_detail_menu_try_chat')}
                                </button>
                                {isOwner && (
                                    <button
                                        type="button"
                                        className="skill-detail-menu-item skill-detail-menu-item--danger"
                                        onClick={() => {
                                            setMenuOpen(false);
                                            setPendingDelete(true);
                                        }}
                                        data-testid="skill-detail-menu-delete"
                                    >
                                        {t('skills_detail_menu_delete')}
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Sem o conteudo (nativa), a descricao e' tudo o que o usuario ve
                da skill - vai inteira, sem o corte de 2 linhas. */}
            <p
                className={`skill-detail-description${canReadContent ? '' : ' skill-detail-description--full'}`}
                data-testid="skill-detail-description"
            >
                {description}
            </p>

            {!canReadContent ? (
                <p className="skill-detail-native-notice" data-testid="skill-detail-native-notice">
                    {t('skills_detail_native_notice')}
                </p>
            ) : loading ? (
                <div className="skill-detail-loading" data-testid="skill-detail-loading">
                    <CircularProgress size={32} thickness={4} />
                </div>
            ) : (
                <div className="skill-detail-body">
                    <div className="skill-detail-file-tree" data-testid="skill-detail-file-tree">
                        {renderNodes(buildFileTree(files))}
                    </div>
                    <div className="skill-detail-content" data-testid="skill-detail-content">
                        {!fileLoading && current?.is_text && hasViewToggle(selectedPath) && !contentTooLargeToPreview && (
                            <div className="skill-detail-view-toggle" data-testid="skill-detail-view-toggle">
                                <button
                                    type="button"
                                    className={`skill-detail-view-toggle-button${viewMode === 'preview' ? ' skill-detail-view-toggle-button--active' : ''}`}
                                    onClick={() => setViewMode('preview')}
                                    aria-label={t('skill_view_toggle_preview_label')}
                                    title={t('skill_view_toggle_preview_label')}
                                    data-testid="skill-detail-view-toggle-preview"
                                >
                                    <img src="/imagens/skills-eye.svg" alt="" />
                                </button>
                                <button
                                    type="button"
                                    className={`skill-detail-view-toggle-button${viewMode === 'code' ? ' skill-detail-view-toggle-button--active' : ''}`}
                                    onClick={() => setViewMode('code')}
                                    aria-label={t('skill_view_toggle_code_label')}
                                    title={t('skill_view_toggle_code_label')}
                                    data-testid="skill-detail-view-toggle-code"
                                >
                                    <img src="/imagens/skills-code.svg" alt="" />
                                </button>
                            </div>
                        )}
                        <div className="skill-detail-content-scroll">
                            {fileLoading || !current ? (
                                <div className="skill-detail-content-loading" data-testid="skill-detail-content-loading">
                                    <CircularProgress size={24} thickness={4} />
                                </div>
                            ) : current.is_image ? (
                                <img
                                    className="skill-detail-content-image"
                                    data-testid="skill-detail-content-image"
                                    src={`data:${current.mimetype};base64,${current.content_base64}`}
                                    alt={selectedPath}
                                />
                            ) : current.is_pdf ? (
                                <embed
                                    className="skill-detail-content-pdf"
                                    data-testid="skill-detail-content-pdf"
                                    // '#toolbar=0' esconde a barra do visualizador nativo (paginas,
                                    // zoom, download, imprimir) - pedido pelo usuario ao validar
                                    // visualmente, ja que a acao de download ja existe em outro lugar
                                    // da tela (menu de opcoes/card).
                                    src={`data:${current.mimetype};base64,${current.content_base64}#toolbar=0`}
                                    type={current.mimetype}
                                />
                            ) : current.is_docx || current.is_xlsx || current.is_pptx ? (
                                officePreviewLoading || officePreview?.path !== selectedPath ? (
                                    <div className="skill-detail-content-loading" data-testid="skill-detail-content-loading">
                                        <CircularProgress size={24} thickness={4} />
                                    </div>
                                ) : officePreview.error ? (
                                    <p className="skill-detail-content-unsupported" data-testid="skill-detail-content-unsupported">
                                        {t('skills_detail_preview_unsupported')}
                                    </p>
                                ) : current.is_docx ? (
                                    <iframe
                                        className="skill-detail-content-html-preview"
                                        data-testid="skill-detail-content-docx-preview"
                                        title={selectedPath}
                                        sandbox=""
                                        srcDoc={officePreview.html || ''}
                                    />
                                ) : current.is_xlsx ? (
                                    <DataTable
                                        rows={officePreview.rows}
                                        className="skill-detail-content-xlsx-table"
                                        testId="skill-detail-content-xlsx-table"
                                    />
                                ) : (
                                    <PptxSlides slides={officePreview.slides} />
                                )
                            ) : !current.is_text ? (
                                <p className="skill-detail-content-unsupported" data-testid="skill-detail-content-unsupported">
                                    {t('skills_detail_preview_unsupported')}
                                </p>
                            ) : contentTooLargeToPreview ? (
                                <div className="skill-detail-content-too-large" data-testid="skill-detail-content-too-large">
                                    <p>
                                        {t('skills_detail_preview_too_large_message', {
                                            size: formatMegabytes(currentTextBytes),
                                        })}
                                    </p>
                                </div>
                            ) : isMarkdownPath(selectedPath) ? (
                                viewMode === 'preview' ? (
                                    <div className="skill-detail-content-markdown" data-testid="skill-detail-content-markdown">
                                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{stripFrontmatter(current.content || '')}</ReactMarkdown>
                                    </div>
                                ) : (
                                    <pre className="skill-detail-content-raw" data-testid="skill-detail-content-raw">{stripFrontmatter(current.content || '')}</pre>
                                )
                            ) : isHtmlPath(selectedPath) ? (
                                viewMode === 'preview' ? (
                                    <iframe
                                        className="skill-detail-content-html-preview"
                                        data-testid="skill-detail-content-html-preview"
                                        title={selectedPath}
                                        sandbox=""
                                        srcDoc={current.content || ''}
                                    />
                                ) : (
                                    <CodeHighlight code={current.content} extension="html" testId="skill-detail-content-code" />
                                )
                            ) : isCsvPath(selectedPath) ? (
                                viewMode === 'preview' ? (
                                    <CsvTable text={current.content || ''} />
                                ) : (
                                    <pre className="skill-detail-content-raw" data-testid="skill-detail-content-raw">{current.content}</pre>
                                )
                            ) : isCodePath(selectedPath) ? (
                                <CodeHighlight code={current.content} extension={extensionOf(selectedPath)} testId="skill-detail-content-code" />
                            ) : (
                                <pre className="skill-detail-content-raw" data-testid="skill-detail-content-raw">{current.content}</pre>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {isOwner && (
                <input
                    type="file"
                    accept=".md,.zip,.skill"
                    ref={replaceInputRef}
                    onChange={handleReplaceFileChange}
                    style={{ display: 'none' }}
                    data-testid="skill-detail-replace-input"
                />
            )}

            <ConfirmDialog
                isOpen={pendingReplaceFile !== null}
                title={t('skills_detail_replace_confirm_title')}
                message={t('skills_detail_replace_confirm_message')}
                confirmLabel={t('skills_replace_confirm_button')}
                cancelLabel={t('skills_replace_cancel_button')}
                onConfirm={handleConfirmReplace}
                onCancel={handleCancelReplace}
            />

            <ConfirmDialog
                isOpen={pendingDelete}
                title={t('skills_detail_delete_confirm_title')}
                message={t('skills_detail_delete_confirm_message')}
                confirmLabel={t('skills_delete_confirm_button')}
                cancelLabel={t('skills_delete_cancel_button')}
                onConfirm={handleConfirmDelete}
                onCancel={handleCancelDelete}
                danger
            />

            {editing && (
                <div
                    className="edit-skill-overlay"
                    onClick={handleEditOverlayClick}
                    data-testid="edit-skill-overlay"
                >
                    <div className="edit-skill-modal" data-testid="edit-skill-modal">
                        <div className="edit-skill-header">
                            <p className="edit-skill-title">{t('skills_detail_edit_title')}</p>
                            <button
                                type="button"
                                className="edit-skill-close"
                                aria-label={t('closeIcon')}
                                onClick={handleCancelEdit}
                                data-testid="edit-skill-close"
                            >
                                <img src="/imagens/x.svg" alt="" />
                            </button>
                        </div>

                        <div className="edit-skill-field">
                            <p className="edit-skill-label">{t('skills_detail_edit_name_label')}</p>
                            <div className="edit-skill-name-input-wrapper">
                                <span className="edit-skill-name-prefix" aria-hidden="true">/</span>
                                <input
                                    type="text"
                                    className="edit-skill-name-input"
                                    value={editName}
                                    onChange={(e) => setEditName(e.target.value)}
                                    data-testid="edit-skill-name-input"
                                />
                            </div>
                        </div>

                        <div className="edit-skill-field">
                            <p className="edit-skill-label">{t('skills_detail_edit_description_label')}</p>
                            <textarea
                                className="edit-skill-textarea"
                                value={editDescription}
                                onChange={(e) => setEditDescription(e.target.value)}
                                data-testid="edit-skill-description-input"
                            />
                        </div>

                        <div className="edit-skill-field">
                            <p className="edit-skill-label">{t('skills_detail_edit_content_label')}</p>
                            <textarea
                                className="edit-skill-textarea edit-skill-textarea--content"
                                value={editContent}
                                onChange={(e) => setEditContent(e.target.value)}
                                data-testid="edit-skill-content-input"
                            />
                        </div>

                        <button
                            type="button"
                            className="edit-skill-save-button"
                            disabled={savingEdit}
                            onClick={handleSaveEdit}
                            data-testid="edit-skill-save"
                        >
                            {t('skills_detail_edit_save_button')}
                        </button>
                    </div>
                </div>
            )}

            {alert && (
                <AlertsCustom
                    type={alert.type}
                    title={alert.title}
                    message={alert.message}
                    onClose={() => setAlert(null)}
                />
            )}
        </div>
    );
}
